
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { FileText, Pencil, Trash2, Upload, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { Team } from "../types";
import { AverageTeamCost } from '@/components/common/AverageTeamCost';
import { TeamEquipmentCost } from '@/components/common/TeamEquipmentCost';
import { TeamGuardEquipmentCost } from '@/components/common/TeamGuardEquipmentCost';
import { cn } from "@/lib/utils";

interface TeamCardProps {
  team: Team;
  onClick: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  onDeleteClick?: (id: string) => void;
}

export const TeamCard = ({ 
  team, 
  onClick,
  canEdit = false,
  canDelete = false,
  onDeleteClick
}: TeamCardProps) => {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(team.name);
  const [description, setDescription] = useState(team.description || "");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Generate subtle variations based on team ID for visual diversity
  const teamVariation = team.id.charCodeAt(0) % 6;
  
  const getGradientVariation = () => {
    switch (teamVariation) {
      case 0:
        return "from-blue-50/80 to-blue-100/60 dark:from-blue-800/90 dark:to-blue-900/80"; // Soft blue
      case 1:
        return "from-purple-50/80 to-purple-100/60 dark:from-purple-800/90 dark:to-purple-900/80"; // Soft purple
      case 2:
        return "from-emerald-50/80 to-emerald-100/60 dark:from-emerald-800/90 dark:to-emerald-900/80"; // Soft emerald
      case 3:
        return "from-orange-50/80 to-orange-100/60 dark:from-orange-800/90 dark:to-orange-900/80"; // Soft orange
      case 4:
        return "from-pink-50/80 to-pink-100/60 dark:from-pink-800/90 dark:to-pink-900/80"; // Soft pink
      case 5:
        return "from-teal-50/80 to-teal-100/60 dark:from-teal-800/90 dark:to-teal-900/80"; // Soft teal
      default:
        return "from-blue-50/80 to-blue-100/60 dark:from-blue-800/90 dark:to-blue-900/80";
    }
  };

  const getBorderColor = () => {
    switch (teamVariation) {
      case 0:
        return "border-blue-200/40 dark:border-blue-600/40";
      case 1:
        return "border-purple-200/40 dark:border-purple-600/40";
      case 2:
        return "border-emerald-200/40 dark:border-emerald-600/40";
      case 3:
        return "border-orange-200/40 dark:border-orange-600/40";
      case 4:
        return "border-pink-200/40 dark:border-pink-600/40";
      case 5:
        return "border-teal-200/40 dark:border-teal-600/40";
      default:
        return "border-blue-200/40 dark:border-blue-600/40";
    }
  };
  
  const handleEditClick = () => {
    setName(team.name);
    setDescription(team.description || "");
    setImageFile(null);
    setIsEditing(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setIsSubmitting(true);
      
      const updateData: Partial<Team> = {
        name,
        description
      };
      
      if (imageFile) {
        const fileExt = imageFile.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `team_images/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('teams')
          .upload(filePath, imageFile);
          
        if (uploadError) {
          throw uploadError;
        }
        
        const { data: urlData } = supabase
          .storage
          .from('teams')
          .getPublicUrl(filePath);
          
        updateData.image_url = urlData.publicUrl;
      }
      
      const { error } = await supabase
        .from('teams')
        .update(updateData)
        .eq('id', team.id);
        
      if (error) throw error;
      
      toast({
        title: "Team updated",
        description: "The team has been updated successfully."
      });
      
      window.location.reload();
    } catch (error: any) {
      toast({
        title: "Failed to update",
        description: error.message || "An error occurred while updating the team.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
      setIsEditing(false);
    }
  };

  return (
    <>
      <Card className={cn(
        "relative overflow-hidden group transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg rounded-xl border-2 h-full flex flex-col bg-background/50",
        getBorderColor()
      )}>
        <div className="relative p-4 sm:p-5 flex flex-col sm:flex-row items-stretch gap-4 h-full">
          <div className="flex items-center sm:items-start gap-3 sm:gap-4 mb-2 sm:mb-0">
            <div className="p-1.5 bg-background/40 text-muted-foreground rounded-full mb-0 backdrop-blur-sm border border-border/60 group-hover:scale-110 transition-transform duration-300 overflow-hidden">
              {team.image_url ? (
                <img 
                  src={team.image_url} 
                  alt={`${team.name} logo`}
                  className="w-5 h-5 object-contain"
                />
              ) : (
                <img 
                  src="/lovable-uploads/643858c8-e787-48c9-990c-8f64b25eaf37.png" 
                  alt="Team icon" 
                  className="w-5 h-5"
                />
              )}
            </div>
            
            <h3 className="font-medium text-base sm:text-lg text-left text-foreground group-hover:text-foreground transition-colors">{team.name}</h3>
          </div>
          
          <div className="flex-1 sm:mb-0 flex flex-col sm:items-start items-center gap-2">
            <img 
              src={team.image_url || "/lovable-uploads/251380b1-4d53-4124-bf15-25f08257a46b.png"} 
              alt="Team illustration" 
              className="w-20 h-20 sm:w-24 sm:h-24 object-contain mb-2 rounded-xl bg-background/20 p-2 backdrop-blur-sm border border-border/30"
            />
            <p className="text-sm text-muted-foreground line-clamp-2 mt-1 sm:text-left text-center">
              {team.description ? team.description : "No description available."}
            </p>
          </div>
          
          <div className="mt-2 sm:mt-0 flex sm:flex-col gap-2 sm:gap-3 sm:w-56">
            {canEdit && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEditClick();
                }}
                className="bg-background/25 border-border/40 text-foreground hover:bg-background hover:text-foreground backdrop-blur-sm transition-all duration-300 font-medium w-full mb-2"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit Card
              </Button>
            )}
            
            {canDelete && onDeleteClick && (
              <Button
                variant="destructive"
                className="w-full bg-red-600/90 hover:bg-red-700 backdrop-blur-sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDeleteClick(team.id);
                }}
              >
                <Trash2 className="w-5 h-5 mr-2" />
                Delete
              </Button>
            )}
            
            <div className="w-full border-t border-border/30 mt-2 pt-2">
              <div className="grid grid-cols-3 gap-2 w-full">
                <div className="flex justify-center">
                  <AverageTeamCost teamId={team.id} />
                </div>
                <div className="flex justify-center">
                  <TeamEquipmentCost teamId={team.id} />
                </div>
                <div className="flex justify-center">
                  <TeamGuardEquipmentCost teamId={team.id} />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <Button 
          className="w-full rounded-none h-10 text-sm sm:text-base bg-background/25 border-border/40 text-foreground hover:bg-background hover:text-foreground backdrop-blur-sm transition-all duration-300 font-medium mt-auto border-t"
          variant="outline"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick();
          }}
        >
          <Eye className="w-5 h-5 mr-2" />
          View Reports
        </Button>
      </Card>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Team</DialogTitle>
            <DialogDescription>
              Make changes to your team here. Click save when you're done.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter team description"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="image">Team Image</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="flex-1"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon" 
                  onClick={() => document.getElementById('image')?.click()}
                >
                  <Upload className="h-4 w-4" />
                </Button>
              </div>
              {imageFile && (
                <p className="text-sm text-muted-foreground">
                  New image selected: {imageFile.name}
                </p>
              )}
              {team.image_url && (
                <div className="mt-2">
                  <p className="text-sm text-muted-foreground mb-2">Current image:</p>
                  <img 
                    src={team.image_url} 
                    alt="Current team image" 
                    className="w-full max-h-32 object-contain border rounded-md"
                  />
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsEditing(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveChanges}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
